import { MISSING_FIELD_LABELS, tryParseMoney } from '@sitzmark/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  Explain,
  Field,
  Loading,
  Mono,
  More,
  Screen,
  SectionNav,
} from '../../components/ui.js';
import { useNavReset } from '../../components/nav-reset.js';
import { useNotice } from '../../components/notice.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { money } from '../../lib/money.js';
import { useQuietUntilTomorrow } from '../../lib/quiet-until-tomorrow.js';
import { useTypingFocus } from '../../lib/typing-focus.js';
import { usePreference } from '../../lib/preferences.js';
import { useLabelPrinter } from '../purchasing/labels.js';
import type { LabelSize } from '../purchasing/labels.js';
import { useReceiptPrinting } from '../sales/receipt.js';
import { AddVariantsDialog, NewStyleScreen } from './style-editor.js';
import { askedFields } from './finish-run.js';
import { detailOutOfSight, paneMaxHeight } from './detail-pane.js';
import { FinishStylesDialog } from './finish-products.js';
import { EditStyleScreen, VariantSettingsDialog } from './style-settings.js';
import { marginPercent, repriceFromMsrp } from './product-matrix.js';
import { AdjustStockDialog } from './adjust-stock.js';
import { StyleLabelsDialog } from './style-labels.js';
import { StylePhotos } from './style-photos.js';
import { sizeGrids } from '../purchasing/size-grid.js';
import { shopPageFor } from '../../lib/shop-link.js';
import './products.css';

/**
 * Products and variants (SCOPE §5.2).
 *
 * The manual path. Anything that arrives in a vendor file comes through the
 * import wizard; this is for everything else — a shop-made bundle, a tub of
 * wax, a style the rep wrote down — and for every price change that follows
 * them through the season.
 *
 * Same anatomy as Customers (§2.2-4): search stays put on the left, the style
 * fills the right. Prices are edited in place across the whole style at once,
 * because repricing is never a one-SKU job.
 */

type Detail = Awaited<ReturnType<typeof api.products.get.query>>;
type Variant = Detail['variants'][number];

export function ProductsScreen({
  canManage,
  nav,
}: {
  canManage: boolean;
  nav?: import('react').ReactNode;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  /*
   * Each press of a row, counted — so pressing the same style again, after
   * scrolling away from it, still brings it back into view.
   */
  const [picks, setPicks] = useState(0);
  const detailRef = useRef<HTMLDivElement>(null);
  const pick = (productId: string) => {
    setSelectedId(productId);
    setPicks((count) => count + 1);
    setScannedVariantId(null);
  };

  /*
   * Scanning a label here opens the style with that size marked (FB-1115):
   * "should be able to scan any existing label and it pull up the product and
   * variant." A scan ends in Enter; so can typing a SKU. The match is the
   * register's — a barcode exactly, then a SKU exactly — so whatever scans at
   * the counter scans here.
   */
  const searchRef = useRef<HTMLInputElement>(null);
  const [scannedVariantId, setScannedVariantId] = useState<string | null>(null);
  const [noLabel, setNoLabel] = useState<string | null>(null);
  const appendTyped = useCallback((character: string) => {
    setTerm((current) => current + character);
  }, []);
  // A scan with the caret anywhere on this screen lands in its search box.
  useTypingFocus(searchRef, appendTyped);
  const lookUp = useMutation({
    mutationFn: (code: string) => api.catalog.search.query({ term: code, limit: 2 }),
    onSuccess: (found, code) => {
      const hit = found.scanned ? found.results[0] : undefined;
      if (hit === undefined) {
        // Not a label: the list below is the search, as it always was.
        setNoLabel(/^\d{6,}$/.test(code) && found.results.length === 0 ? code : null);
        return;
      }
      recordBreadcrumb('stock', 'scanned a label');
      setNoLabel(null);
      setTerm('');
      setSelectedId(hit.productId);
      setPicks((count) => count + 1);
      setScannedVariantId(hit.variantId);
    },
  });

  /*
   * A picked style shows (2026-10-01): it starts at its own top, and when it
   * is out of sight — stacked under a long list on a narrow screen — the page
   * goes to it. Wide, the pane is pinned beside the list (products.css) and
   * already in view, so nothing moves.
   */
  useEffect(() => {
    const pane = detailRef.current;
    if (picks === 0 || pane === null) return;
    pane.scrollTop = 0;
    const { top, bottom } = visibleArea(pane);
    // Only stacked: wide, the pane is pinned beside the list and moving the
    // page would only lose the place in it.
    const stacked = !window.matchMedia(WIDE).matches;
    if (stacked && detailOutOfSight(pane.getBoundingClientRect().top, top, bottom)) {
      const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      pane.scrollIntoView({ block: 'start', behavior: still ? 'auto' : 'smooth' });
    }
  }, [picks]);

  /*
   * The pinned pane ends just above the action bar wherever it starts, so the
   * Save bar at its foot is always in view (detail-pane.ts). Measured again
   * on scroll and resize, and whenever the page's height changes — the
   * reminder going, the filters opening. Again after New style, which
   * replaces the screen and so the pane.
   */
  useEffect(() => {
    const pane = detailRef.current;
    if (pane === null) return;
    const wide = window.matchMedia(WIDE);
    let frame = 0;
    const fit = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!wide.matches) {
          pane.style.maxHeight = '';
          return;
        }
        const pinnedTop = parseFloat(getComputedStyle(pane).top) || 0;
        pane.style.maxHeight = `${paneMaxHeight(
          pane.getBoundingClientRect().top,
          pinnedTop,
          visibleArea(pane).bottom,
          PANE_GAP,
        )}px`;
      });
    };
    fit();
    window.addEventListener('scroll', fit, { passive: true });
    window.addEventListener('resize', fit);
    wide.addEventListener('change', fit);
    const resized = new ResizeObserver(fit);
    resized.observe(document.documentElement);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', fit);
      window.removeEventListener('resize', fit);
      wide.removeEventListener('change', fit);
      resized.disconnect();
    };
  }, [creating]);
  const [finishing, setFinishing] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  /** Filters on the sizes, all true of one size together (FB-1052). */
  const [sizeFilter, setSizeFilter] = useState({
    color: '',
    size: '',
    minPrice: '',
    maxPrice: '',
    inStock: false,
  });
  const [debouncedSizes, setDebouncedSizes] = useState(sizeFilter);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSizes(sizeFilter), 200);
    return () => clearTimeout(timer);
  }, [sizeFilter]);
  const sizesInput = (() => {
    const min = tryParseMoney(debouncedSizes.minPrice);
    const max = tryParseMoney(debouncedSizes.maxPrice);
    const input = {
      ...(debouncedSizes.color.trim() === '' ? {} : { color: debouncedSizes.color.trim() }),
      ...(debouncedSizes.size.trim() === '' ? {} : { size: debouncedSizes.size.trim() }),
      ...(min === null ? {} : { minPriceCents: min }),
      ...(max === null ? {} : { maxPriceCents: max }),
      ...(debouncedSizes.inStock ? { inStock: true } : {}),
    };
    return Object.keys(input).length === 0 ? undefined : input;
  })();
  /*
   * Quiet until tomorrow, never for good (item 39: "dismissible, never
   * blocking"). It used to be the tab's session, which on a till that is
   * never closed meant forever — and with it went the only way into the pass
   * (2026-10-01). Finish styles beside the list is that way in now.
   */
  const [bannerDismissed, dismissBanner] = useQuietUntilTomorrow('finish-styles-banner');

  // Pressing Products (or Stock) while already here goes back to the start
  // (item 31): the search stays — typed work — but the drill closes.
  useNavReset(() => {
    setSelectedId(null);
    setCreating(false);
    setFinishing(false);
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 150);
    return () => clearTimeout(timer);
  }, [term]);

  const taxonomy = useQuery({
    queryKey: ['products', 'taxonomy'],
    queryFn: () => api.products.taxonomy.query(),
    staleTime: 300_000,
  });

  const list = useQuery({
    queryKey: ['products', 'list', debounced, departmentId, showInactive, sizesInput],
    queryFn: () =>
      api.products.list.query({
        term: debounced,
        ...(departmentId === '' ? {} : { departmentId }),
        includeInactive: showInactive,
        ...(sizesInput === undefined ? {} : { sizes: sizesInput }),
      }),
  });

  // The banner's count only — the pass fetches its own queue when opened.
  const debt = useQuery({
    queryKey: ['products', 'unfinished', 'count'],
    queryFn: () => api.products.unfinished.query({ limit: 1 }),
    enabled: canManage,
    staleTime: 60_000,
  });

  const rows = list.data ?? [];

  // Creating a style takes over the section rather than floating above it: it
  // is a task with its own workspace, and the grid needs the room (§2.2-4).
  if (creating) {
    return (
      <NewStyleScreen
        onClose={() => setCreating(false)}
        onCreated={(productId) => {
          setCreating(false);
          setSelectedId(productId);
        }}
      />
    );
  }

  return (
    <Screen
      title="Products"
      subtitle="Everything the shop sells, and what it sells for."
      // A style's whole variant table has to be readable without scrolling
      // sideways — the action lives in the last column.
      width="wide"
      primaryAction={
        canManage ? (
          <Button variant="primary" onClick={() => setCreating(true)}>
            New model
          </Button>
        ) : undefined
      }
    >
      {nav}
      {/*
        The finishing debt, said once and dismissible (item 39). A banner
        rather than a gate: the register sells an unfinished style fine; this
        is the reminder that an evening's pass is owed, with the pass one
        press away.
      */}
      {canManage && !bannerDismissed && (debt.data?.total ?? 0) > 0 ? (
        <Callout tone="info">
          <div className="finish-banner">
            <span>
              {debt.data!.total} model{debt.data!.total === 1 ? '' : 's'} still need
              {debt.data!.total === 1 ? 's' : ''} finishing — a price, a bin, a season or a filing
              the vendor file could not know.
            </span>
            <span className="finish-banner__actions">
              <Button variant="primary" onClick={() => setFinishing(true)}>
                Finish them
              </Button>
              <Button variant="ghost" onClick={dismissBanner}>
                Not now
              </Button>
            </span>
          </div>
        </Callout>
      ) : null}
      {finishing ? <FinishStylesDialog onClose={() => setFinishing(false)} /> : null}
      <div className="products">
        <div className="products__list">
          <input
            ref={searchRef}
            className="search__input"
            aria-label="Search model #, name, brand, SKU, or scan a label"
            placeholder="Search model #, name, brand, SKU — or scan a label"
            value={term}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => {
              setTerm(event.target.value);
              setNoLabel(null);
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' || term.trim() === '') return;
              event.preventDefault();
              lookUp.mutate(term.trim());
            }}
          />
          {noLabel !== null ? (
            <Callout tone="warning">No size here has the SKU or UPC {noLabel}.</Callout>
          ) : null}

          <select
            className="select"
            aria-label="Category"
            value={departmentId}
            onChange={(event) => setDepartmentId(event.target.value)}
          >
            {/* A category takes everything beneath it (decision 214). */}
            <option value="">Every category</option>
            {(taxonomy.data?.departments ?? []).map((category) => (
              <option key={category.id} value={category.id}>
                {category.path}
              </option>
            ))}
          </select>

          {/*
            Switched-off styles are left out, so the list is what the shop
            sells — but one switched off by mistake has to be findable to be
            switched back on (2026-09-25; nothing on screen could reach it).
          */}
          <label className="toggle">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(event) => setShowInactive(event.target.checked)}
            />
            <span>Show inactive models</span>
          </label>

          {/* The pass is always one press away: with the reminder quiet, this
              is the way in (2026-10-01 — there was none). */}
          {canManage && bannerDismissed && (debt.data?.total ?? 0) > 0 ? (
            <div className="products__finish">
              <Button variant="secondary" onClick={() => setFinishing(true)}>
                Finish models
              </Button>
              <span className="text-secondary">{debt.data!.total} still need something</span>
            </div>
          ) : null}

          {/* By size, not by style (FB-1052): price over $20, one color, an L
              in one department, an S anywhere — each true of one size. */}
          <More label="Filter sizes">
            <div className="products__filters">
              <Field label="Color">
                {(props) => (
                  <input
                    {...props}
                    className="input"
                    placeholder="Black"
                    value={sizeFilter.color}
                    onChange={(event) =>
                      setSizeFilter({ ...sizeFilter, color: event.target.value })
                    }
                  />
                )}
              </Field>
              <Field label="Size">
                {(props) => (
                  <input
                    {...props}
                    className="input"
                    placeholder="L"
                    value={sizeFilter.size}
                    onChange={(event) => setSizeFilter({ ...sizeFilter, size: event.target.value })}
                  />
                )}
              </Field>
              <Field label="Price from">
                {(props) => (
                  <input
                    {...props}
                    className="input money"
                    inputMode="decimal"
                    placeholder="20.00"
                    value={sizeFilter.minPrice}
                    onChange={(event) =>
                      setSizeFilter({ ...sizeFilter, minPrice: event.target.value })
                    }
                  />
                )}
              </Field>
              <Field label="Price to">
                {(props) => (
                  <input
                    {...props}
                    className="input money"
                    inputMode="decimal"
                    value={sizeFilter.maxPrice}
                    onChange={(event) =>
                      setSizeFilter({ ...sizeFilter, maxPrice: event.target.value })
                    }
                  />
                )}
              </Field>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={sizeFilter.inStock}
                  onChange={(event) =>
                    setSizeFilter({ ...sizeFilter, inStock: event.target.checked })
                  }
                />
                <span>In stock only</span>
              </label>
            </div>
          </More>

          {list.isError ? <Callout tone="danger">{errorMessage(list.error)}</Callout> : null}

          {list.isPending ? (
            <Loading label="Loading products" />
          ) : rows.length === 0 ? (
            <Card>
              <p className="text-secondary">
                {sizesInput !== undefined
                  ? 'No size matches those filters.'
                  : debounced === ''
                    ? 'No products yet.'
                    : `Nothing matches “${debounced}”.`}
              </p>
            </Card>
          ) : (
            <Card padded={false}>
              <ul className="product-results">
                {rows.map((product) => (
                  <li key={product.id}>
                    <button
                      type="button"
                      className={`product-result${product.id === selectedId ? ' product-result--active' : ''}`}
                      onClick={() => pick(product.id)}
                    >
                      <span className="product-result__name">
                        {product.name}
                        {!product.active ? <Badge tone="warning">Inactive</Badge> : null}
                        {/* The visible marker (item 39): what is still owed,
                            obvious without opening the pass. Finished styles
                            get no ink (§2.2-8). */}
                        {product.missing.length > 0 ? <Badge tone="info">Unfinished</Badge> : null}
                      </span>
                      <span className="product-result__meta">
                        <Mono>{product.styleNumber}</Mono>
                        {product.brand !== null ? ` · ${product.brand}` : ''}
                        {product.matchingSizes != null
                          ? ` · ${product.matchingSizes} of ${product.variantCount} sizes match`
                          : ` · ${product.variantCount} variant${product.variantCount === 1 ? '' : 's'}`}
                      </span>
                      <span className="product-result__price money">
                        {priceRange(product.minRetailCents, product.maxRetailCents)}
                      </span>
                      <span className="product-result__stock num">
                        {product.trackInventory ? `${product.onHand} on hand` : 'Service'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        <div
          className={`products__detail${selectedId === null ? '' : ' products__detail--open'}`}
          ref={detailRef}
        >
          {selectedId === null ? (
            <Card>
              <p className="text-secondary">Pick a model to see its sizes, prices and stock.</p>
            </Card>
          ) : (
            <StyleDetail
              productId={selectedId}
              canManage={canManage}
              scannedVariantId={scannedVariantId}
              onClose={() => setSelectedId(null)}
            />
          )}
        </div>
      </div>
    </Screen>
  );
}

/** Where the pane sits beside the list: products.css's breakpoint. */
const WIDE = '(min-width: 1001px)';
/** The space kept above the action bar — --space-4. */
const PANE_GAP = 16;

/**
 * What shows of the page: under the top bar, above *this* screen's action
 * bar. Other sections stay mounted, hidden, with action bars of their own — a
 * document-wide lookup found one of those, zero pixels tall at the top of the
 * page, and squeezed the pane to its minimum (found driving it, 2026-10-01).
 *
 * And the screen's *own* bar, a child of it — never one inside the pane. Edit
 * style opens a screen of its own in the pane, with a Save bar; the first bar
 * in the screen was that one, so the pane was sized to end above its own Save
 * bar, which then moved up with it, and every scroll shrank it again until it
 * was a sliver ("the item information shrinks way down … when you scroll up it
 * stays shrunk", client 2026-10-15).
 */
function visibleArea(pane: HTMLElement): { top: number; bottom: number } {
  const top = document.querySelector('.topbar')?.getBoundingClientRect().bottom ?? 0;
  const bar = pane
    .closest('.screen')
    ?.querySelector(':scope > .screen__actions')
    ?.getBoundingClientRect();
  const barTop = bar !== undefined && bar.height > 0 ? bar.top : window.innerHeight;
  return { top, bottom: Math.min(barTop, window.innerHeight) };
}

function priceRange(min: number, max: number): string {
  return min === max ? money(min) : `${money(min)} – ${money(max)}`;
}

// ---------------------------------------------------------------------------

function StyleDetail({
  productId,
  canManage,
  scannedVariantId = null,
  onClose,
}: {
  productId: string;
  canManage: boolean;
  /** The size whose label was just scanned, marked in the table (FB-1115). */
  scannedVariantId?: string | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const notify = useNotice();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  /** Grid or list, as this person last left it — wherever they sign in (FB-1113, decision 203). */
  const [sizesView, setSizesView] = usePreference('style-sizes-view', 'grid');
  /** What each box in the grid shows besides on hand, as this person chose. */
  const [gridShow, setGridShow] = usePreference('sizes-grid-show', DEFAULT_GRID_SHOW);
  /** Units just added by an adjustment, offered as labels (FB-1116). */
  const [addedLabels, setAddedLabels] = useState<Record<string, number> | null>(null);
  /** Which variant's full settings are open, if any. */
  const [openVariantId, setOpenVariantId] = useState<string | null>(null);
  /** Retail prices being edited, keyed by variant. Empty means untouched. */
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const printing = useReceiptPrinting();
  const labels = useLabelPrinter();

  const detail = useQuery({
    queryKey: ['products', 'get', productId],
    queryFn: () => api.products.get.query({ productId }),
  });

  /**
   * On the web shop, or not (decision 148).
   *
   * Beside the style's other verbs, because "is this for sale online?" is a
   * fact about the style like its department — and the address it is given is
   * shown, because whoever publishes it will be asked where the page is.
   */
  const publish = useMutation({
    mutationFn: (published: boolean) => api.products.setPublished.mutate({ productId, published }),
    onSuccess: (result) => {
      recordBreadcrumb(
        'products',
        result.published ? 'put a model on the web' : 'took a model off the web',
      );
      const styleName = detail.data?.product.name ?? 'The model';
      notify({
        title: result.published
          ? `${styleName} is on the web shop`
          : `${styleName} is off the web shop`,
      });
      void queryClient.invalidateQueries({ queryKey: ['products', 'get', productId] });
      void queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
    },
  });

  const save = useMutation({
    mutationFn: (patches: { variantId: string; retailCents: number }[]) =>
      api.products.updateVariants.mutate({ productId, patches }),
    onSuccess: (updated, patches) => {
      recordBreadcrumb('products', 'repriced a model');
      notify({
        title: `Saved — ${updated.product.name}, ${patches.length} ${
          patches.length === 1 ? 'price' : 'prices'
        }`,
      });
      queryClient.setQueryData(['products', 'get', productId], updated);
      void queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      // A price can be what made it finished.
      void queryClient.invalidateQueries({ queryKey: ['products', 'unfinished'] });
      setDrafts({});
    },
  });

  useEffect(() => {
    // A different style is a different set of edits; carrying them across would
    // apply one style's prices to another's SKUs — or print one style's label
    // run against another's sizes.
    setDrafts({});
    setEditing(false);
    setOpenVariantId(null);
    setLabelsOpen(false);
  }, [productId]);

  if (detail.isPending) return <Loading label="Loading the model" />;
  if (detail.isError) return <Callout tone="danger">{errorMessage(detail.error)}</Callout>;

  const { product, variants } = detail.data;

  const applyDetail = (updated: typeof detail.data) => {
    queryClient.setQueryData(['products', 'get', productId], updated);
    void queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
    // The banner's count and the order's reminders follow every edit to a style
    // (2026-10-03): it stayed on its cached count after Edit style cleared a
    // season, and stayed up after one was filled in, until the cache aged out.
    void queryClient.invalidateQueries({ queryKey: ['products', 'unfinished'] });
  };

  // Editing the style takes over the pane, like New Style takes over the
  // section: it is a form with real work in it, not a question (§2.2-4).
  if (editing) {
    return (
      <EditStyleScreen
        product={product}
        onClose={() => setEditing(false)}
        onSaved={(updated) => {
          applyDetail(updated);
          setEditing(false);
        }}
        onDeleted={() => {
          void queryClient.invalidateQueries({ queryKey: ['products'] });
          onClose();
        }}
      />
    );
  }

  /** Only the prices that actually parsed and actually changed. */
  const patches = Object.entries(drafts).flatMap(([variantId, raw]) => {
    const parsed = tryParseMoney(raw);
    const current = variants.find((variant) => variant.id === variantId);
    if (parsed === null || current === undefined || parsed === current.retailCents) return [];
    return [{ variantId, retailCents: parsed }];
  });

  const unparseable = Object.values(drafts).some(
    (raw) => raw.trim() !== '' && tryParseMoney(raw) === null,
  );

  const openVariant = variants.find((variant) => variant.id === openVariantId);
  const owed = askedFields(product, variants);

  const reprice = (percentOfMsrp: number) => {
    const priced = repriceFromMsrp(variants, percentOfMsrp);
    setDrafts(
      Object.fromEntries(
        priced.map((entry) => [entry.variantId, (entry.retailCents / 100).toFixed(2)]),
      ),
    );
  };

  return (
    <div className="style">
      {labels.frame}
      <Card
        title={product.name}
        actions={
          <div className="style__actions">
            {/*
              Two verbs, kept apart on purpose. "Edit style" is about the style
              itself — its name, number, vendor, department, whether it is still
              stocked. "Add colours & sizes" is about what sizes of it exist.
              Folding the first into the second is what made staff ask where the
              settings were, because the answer was nowhere (§2.2-3).
            */}
            {canManage ? (
              <>
                <Button
                  variant="ghost"
                  busy={publish.isPending}
                  onClick={() => publish.mutate(!product.webEnabled)}
                >
                  {product.webEnabled ? 'Take off the web shop' : 'Sell online'}
                </Button>
                <Button variant="ghost" onClick={() => setEditing(true)}>
                  Edit model
                </Button>
                <Button variant="ghost" onClick={() => setAdding(true)}>
                  Colors &amp; sizes
                </Button>
              </>
            ) : null}
            {/*
              The third verb, and not gated on `catalog.manage` like the other
              two: printing a label changes nothing, and the person who finds a
              tag missing off a jacket is whoever is on the floor. Until now
              labels could be printed from the receiving bench alone, so an item
              the shop never received through us — after the import, most of the
              catalog — had no route to one at all.
            */}
            <Button
              variant="ghost"
              disabledReason={printing === null ? 'Loading label settings' : undefined}
              onClick={() => setLabelsOpen(true)}
            >
              Print labels
            </Button>
            {/*
              Fourth verb, same floor as printing (§6.4, decision 102): the
              person who finds the torn jacket is whoever is on the floor.
              The server elevates to `inventory.adjust` — a manager PIN
              inline, never a dead end — because the failure prevented is an
              unrecorded loss, not an unauthorised one.
            */}
            {product.trackInventory ? (
              <Button variant="ghost" onClick={() => setAdjusting(true)}>
                Adjust stock
              </Button>
            ) : null}
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        }
      >
        <div className="style__facts">
          <Mono>{product.styleNumber}</Mono>
          {product.vendor !== null ? <span>{product.vendor}</span> : null}
          {product.brand !== null ? <span>{product.brand}</span> : null}
          {product.department !== null ? <span>{product.department}</span> : null}
          {product.season !== null ? <Badge tone="neutral">{product.season}</Badge> : null}
          {!product.trackInventory ? <Badge tone="info">Not stocked</Badge> : null}
          {product.finalSale ? <Badge tone="warning">Final sale</Badge> : null}
          {product.webEnabled ? <Badge tone="info">On the web shop</Badge> : null}
          {product.webEnabled && product.inStoreOnly ? (
            <Badge tone="neutral">In store only</Badge>
          ) : null}
          {!product.ships ? <Badge tone="neutral">Pickup only</Badge> : null}
          {!product.active ? <Badge tone="warning">Inactive</Badge> : null}
        </div>
        {product.description !== null ? (
          <p className="text-secondary style__description">{product.description}</p>
        ) : null}
        {product.webEnabled && product.webSlug !== null ? (
          <p className="text-secondary style__web">
            {/* The address is the way to the page itself, as a customer sees
                it — in a new tab, so this style's card keeps its place. */}
            On the web shop at{' '}
            <a href={shopPageFor(product.webSlug)} target="_blank" rel="noopener">
              <Mono>/p/{product.webSlug}</Mono>
            </a>
          </p>
        ) : null}
        {publish.isError ? <Callout tone="danger">{errorMessage(publish.error)}</Callout> : null}
        {/* Names the debt in words, so the fix does not require the pass. */}
        {owed.length > 0 ? (
          <p className="text-secondary style__missing">
            Still needs: {owed.map((field) => MISSING_FIELD_LABELS[field]).join(', ')}.
          </p>
        ) : null}
      </Card>

      <StylePhotos
        productId={productId}
        styleName={product.name}
        variants={variants}
        canManage={canManage}
      />

      <Card
        title={`${variants.length} variant${variants.length === 1 ? '' : 's'}`}
        padded={false}
        actions={
          canManage && sizesView === 'list' ? (
            <div className="style__reprice">
              <span className="text-secondary">Retail =</span>
              {[100, 95, 90].map((percent) => (
                <button
                  key={percent}
                  type="button"
                  className="chip"
                  onClick={() => reprice(percent)}
                >
                  {percent}% of MSRP
                </button>
              ))}
            </div>
          ) : undefined
        }
      >
        {/* As a grid by default (FB-1113): "on this screen we have a list of
            variants, can we have it show a grid view?" The list keeps what only
            it does — every column, and retail typed inline. */}
        <div className="style__sizes-view">
          <SectionNav
            label="Show the sizes as"
            current={sizesView}
            items={[
              { key: 'grid', label: 'Grid' },
              { key: 'list', label: 'List' },
            ]}
            onChange={setSizesView}
          />
        </div>
        {sizesView === 'grid' ? <GridShowChoice shown={gridShow} onChange={setGridShow} /> : null}
        {sizesView === 'grid' ? (
          <>
            <SizesGrid
              variants={variants}
              shown={gridShow}
              scannedVariantId={scannedVariantId}
              onOpen={canManage ? (id) => setOpenVariantId(id) : null}
            />
            {/* Where a size's own details are (FB-1131: "I edit Sizes, not see
                where is Mfr# anywhere") — nothing said a box could be pressed. */}
            {canManage ? (
              <p className="settings__note">
                Press a size to change its Mfr #, barcode, length, bin or stock targets.
              </p>
            ) : null}
          </>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Color</th>
                  <th>Size</th>
                  {/*
                  No barcode column. It is a 14-digit GTIN in monospace — the
                  widest thing in the table — and reading one off a screen is
                  not something anyone does; scanning is. It lives in the row's
                  Edit dialog, and the only state worth surfacing here is its
                  *absence*, which is flagged beside the SKU (§2.2-8: peripheral
                  status via badges, and no ink for the normal case).
                */}
                  <th className="num">Cost</th>
                  <th className="num">MSRP</th>
                  <th className="num">Retail</th>
                  <th className="num">
                    Margin
                    <Explain label="margin">
                      Retail less cost, as a share of retail — what the shop keeps on a sale, not
                      the markup on cost. Cost is the weighted moving average from receiving.
                    </Explain>
                  </th>
                  <th className="num">On hand</th>
                  <th className="table__actions" />
                </tr>
              </thead>
              <tbody>
                {variants.map((variant) => (
                  <VariantRow
                    key={variant.id}
                    variant={variant}
                    scanned={variant.id === scannedVariantId}
                    canManage={canManage}
                    draft={drafts[variant.id]}
                    onDraft={(value) =>
                      setDrafts((current) => ({ ...current, [variant.id]: value }))
                    }
                    onOpen={() => setOpenVariantId(variant.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {save.isError ? <Callout tone="danger">{errorMessage(save.error)}</Callout> : null}

      {/* Absent until there is something to save (§2.2-13). */}
      {canManage && (patches.length > 0 || unparseable) ? (
        <div className="style__save">
          <Button variant="ghost" onClick={() => setDrafts({})}>
            Discard changes
          </Button>
          <Button
            variant="primary"
            busy={save.isPending}
            disabledReason={unparseable ? 'One of those prices is not a number' : undefined}
            onClick={() => save.mutate(patches)}
          >
            Save {patches.length} price{patches.length === 1 ? '' : 's'}
          </Button>
        </div>
      ) : null}

      {adding ? (
        <AddVariantsDialog
          productId={productId}
          existing={variants}
          onClose={() => setAdding(false)}
          onAdded={(updated) => {
            applyDetail(updated);
            setAdding(false);
          }}
        />
      ) : null}

      {/* Looked up from the live list, never held as a copy — the same rule the
          register's line dialog follows, so a second edit is not computed
          against a stale row. */}
      {openVariant !== undefined ? (
        <VariantSettingsDialog
          productId={productId}
          variant={openVariant}
          onClose={() => setOpenVariantId(null)}
          onSaved={applyDetail}
        />
      ) : null}

      {adjusting ? (
        <AdjustStockDialog
          productId={productId}
          styleName={product.name}
          variants={variants
            .filter((variant) => variant.active)
            .map((variant) => ({
              id: variant.id,
              sku: variant.sku,
              colorName: variant.colorName,
              sizeLabel: variant.sizeLabel,
              altSize: variant.altSize,
              sizeSort: variant.sizeSort,
              onHand: variant.stock.onHand,
            }))}
          onClose={() => setAdjusting(false)}
          // Labels are only offered where a label printer is set up; anywhere
          // else the dialog's own notice says what was added.
          onAdded={printing === null ? undefined : setAddedLabels}
        />
      ) : null}

      {addedLabels !== null && !labelsOpen && printing !== null ? (
        <Callout tone="info">
          <div className="finish-banner">
            <span>
              {Object.values(addedLabels).reduce((sum, units) => sum + units, 0)} added to stock.
              Labels for them?
            </span>
            <span className="finish-banner__actions">
              <Button variant="primary" onClick={() => setLabelsOpen(true)}>
                Print labels for what was added
              </Button>
              <Button variant="ghost" onClick={() => setAddedLabels(null)}>
                No labels
              </Button>
            </span>
          </div>
        </Callout>
      ) : null}

      {labelsOpen && printing !== null ? (
        <StyleLabelsDialog
          productName={product.name}
          variants={variants}
          size={printing.label.size as LabelSize}
          showPrice={printing.label.showPrice}
          showBothPrices={printing.label.showBothPrices}
          printer={labels}
          initialQuantities={addedLabels ?? undefined}
          onClose={() => {
            setLabelsOpen(false);
            setAddedLabels(null);
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

function VariantRow({
  variant,
  scanned = false,
  canManage,
  draft,
  onDraft,
  onOpen,
}: {
  variant: Variant;
  /** Its label was just scanned: marked, and brought into view (FB-1115). */
  scanned?: boolean;
  canManage: boolean;
  draft: string | undefined;
  onDraft: (value: string) => void;
  onOpen: () => void;
}) {
  const shown = draft ?? (variant.retailCents / 100).toFixed(2);
  const parsed = tryParseMoney(shown);
  const retail = parsed ?? variant.retailCents;
  const margin = marginPercent(variant.costCents, retail);
  const changed = draft !== undefined && parsed !== null && parsed !== variant.retailCents;
  const rowRef = useRef<HTMLTableRowElement>(null);
  useEffect(() => {
    if (scanned) rowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [scanned]);

  return (
    <tr
      ref={rowRef}
      aria-current={scanned ? 'true' : undefined}
      className={`${changed ? 'variant-row--changed' : ''}${variant.active ? '' : ' variant-row--inactive'}${scanned ? ' variant-row--scanned' : ''}`}
    >
      <td className="variants__sku">
        <Mono>{variant.sku}</Mono>
        {!variant.active ? <Badge tone="warning">Inactive</Badge> : null}
        {/* An item with no barcode cannot be scanned at the register, which is
            found out at the counter with a customer waiting unless it is said
            here. Having one is the normal case and gets no ink. */}
        {/* The vendor's UPC is missing, which is most sizes and nothing to fix:
            the SKU label scans. Neutral, and named for what is missing — "No
            barcode" in amber on 90% of sizes read as "cannot be sold" (FB-1070). */}
        {variant.barcode === null ? <Badge tone="neutral">No UPC</Badge> : null}
        {variant.vendorPartNumber != null && variant.vendorPartNumber !== '' ? (
          <div className="text-secondary">
            Mfr # <Mono>{variant.vendorPartNumber}</Mono>
          </div>
        ) : null}
      </td>
      <td>{variant.colorName ?? <span className="text-secondary">—</span>}</td>
      {/* Both size axes. Without the second, a 12 Short and a 12 Regular are two
          identical rows on the one screen a buyer manages a style from — and two
          identical labels off it (decision 89). */}
      <td>
        {[variant.sizeLabel, variant.altSize].filter(Boolean).join(' · ') || (
          <span className="text-secondary">—</span>
        )}
      </td>
      <td className="num money">{money(variant.costCents)}</td>
      <td className="num money">
        {variant.msrpCents === null ? (
          <span className="text-secondary">—</span>
        ) : (
          money(variant.msrpCents)
        )}
      </td>
      <td className="num">
        {canManage ? (
          <input
            className="price-input money"
            inputMode="decimal"
            aria-label={`Retail price for ${variant.sku}`}
            value={shown}
            onChange={(event) => onDraft(event.target.value)}
          />
        ) : (
          <span className="money">{money(variant.retailCents)}</span>
        )}
      </td>
      {/* Updates as the price is typed: the margin is the number actually
          being decided, and making someone save to see it is how a style ends
          up priced below cost (§2.2-10). */}
      <td className={`num${margin !== null && margin < 0 ? ' margin--negative' : ''}`}>
        {margin === null ? '—' : `${margin.toFixed(0)}%`}
      </td>
      <td className="num">
        {variant.stock.onHand}
        {/* Held is on the shelf and not for sale — a web cart, an online
            order, a layaway (FB-1082); on order is what is coming (FB-1064). */}
        {variant.stock.reserved > 0 ? (
          <div className="text-secondary">{variant.stock.reserved} held</div>
        ) : null}
        {variant.stock.onOrder > 0 ? (
          <div className="text-secondary">+{variant.stock.onOrder} on order</div>
        ) : null}
      </td>
      <td className="num table__actions">
        {/* Everything about one size that is not its price: barcode, stock
            targets, bin, whether it is still sold, and removing it. The price
            column stays inline because it is the one that changes weekly. */}
        {canManage ? (
          // "Edit size", not "Edit": reviewers looked under *Edit style* for a
          // size's minimum and cost history, twice (2026-09-10, 2026-09-30).
          <Button variant="ghost" onClick={onOpen}>
            Edit size
          </Button>
        ) : null}
      </td>
    </tr>
  );
}

/**
 * The style's sizes laid out the way an order form is (FB-1064, FB-1072,
 * FB-1078, FB-1113): colors down, sizes across, one grid per length — "1 grid
 * for short, 1 grid for regular, and 1 grid for long" was Steve's own idea.
 * Each box is what is on the shelf, with what is coming and what is held under
 * it, and its SKU and price — so "do we have a 12 Short in black, and is more
 * on the way?" is one look instead of a scroll down sixty rows. Pressed, a box
 * opens that size, as Edit size does in the list.
 *
 * Every style gets one, one-size styles included: a bag in two colors is two
 * boxes (FB-1119 looked for its two sizes and could not see them).
 */
/** What a grid box can show besides on hand — the list's columns (client, 2026-10-15). */
type GridField = 'onHand' | 'retail' | 'cost' | 'margin' | 'msrp' | 'sku';
const GRID_FIELDS: readonly { key: Exclude<GridField, 'onHand'>; label: string }[] = [
  { key: 'sku', label: 'SKU' },
  { key: 'retail', label: 'Retail' },
  { key: 'cost', label: 'Cost' },
  { key: 'margin', label: 'Margin' },
  { key: 'msrp', label: 'MSRP' },
];
/** As the grid has always shown: on hand, the SKU and the price. */
const DEFAULT_GRID_SHOW: GridField[] = ['onHand', 'sku', 'retail'];

/**
 * "In Grid view … how can we intuitively show the other fields that are shown
 * in list (cost, margin, etc)" (client, 2026-10-15). A row of toggles over the
 * grid, each a field the list has; on hand is always there. Kept with the
 * person (decision 203), so a buyer who wants cost and margin sees them at
 * every register, and the floor keeps its simpler grid.
 */
function GridShowChoice({
  shown,
  onChange,
}: {
  shown: readonly GridField[];
  onChange: (next: GridField[]) => void;
}) {
  return (
    <div className="grid-show" role="group" aria-label="Show in each box">
      <span className="text-secondary">Show</span>
      {GRID_FIELDS.map((field) => {
        const on = shown.includes(field.key);
        return (
          <button
            key={field.key}
            type="button"
            className={`chip${on ? ' chip--on' : ''}`}
            aria-pressed={on}
            onClick={() =>
              onChange([
                'onHand',
                ...GRID_FIELDS.map((entry) => entry.key).filter((key) =>
                  key === field.key ? !on : shown.includes(key),
                ),
              ])
            }
          >
            {field.label}
          </button>
        );
      })}
    </div>
  );
}

function SizesGrid({
  variants,
  shown,
  scannedVariantId,
  onOpen,
}: {
  /** The fields each box shows besides on hand. */
  shown: readonly GridField[];
  variants: readonly {
    id: string;
    sku: string;
    active: boolean;
    colorName: string | null;
    sizeLabel: string | null;
    altSize: string | null;
    sizeSort: number;
    retailCents: number;
    costCents: number;
    msrpCents: number | null;
    stock: { onHand: number; reserved: number; onOrder: number };
  }[];
  scannedVariantId: string | null;
  /** Null when this person cannot change a size: the boxes only show. */
  onOpen: ((variantId: string) => void) | null;
}) {
  const live = variants.filter((variant) => variant.active);
  const grids = sizeGrids(live);
  if (live.length === 0) {
    return <p className="text-secondary style__sizes-empty">No active sizes.</p>;
  }

  return (
    <>
      {grids.map((grid) => (
        <div className="table-scroll" key={grid.altSize ?? 'one'}>
          <table className="table matrix">
            {grid.label !== null ? (
              <caption className="matrix__caption">{grid.label}</caption>
            ) : null}
            <thead>
              <tr>
                <th />
                {grid.sizes.map((size, index) => (
                  <th key={size ?? `size-${index}`} scope="col">
                    {size ?? 'One size'}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.rows.map((row) => (
                <tr key={row.key}>
                  <th scope="row" className="matrix__row-head">
                    {row.label}
                  </th>
                  {row.cells.map((variant, index) => {
                    if (variant === undefined) return <td key={index} />;
                    const scanned = variant.id === scannedVariantId;
                    const where = [row.label, variant.sizeLabel ?? 'one size', grid.altSize]
                      .filter((part) => part !== null)
                      .join(' ');
                    const inside = (
                      <>
                        <span
                          className={
                            variant.stock.onHand > 0
                              ? 'size-box__count'
                              : 'size-box__count text-secondary'
                          }
                        >
                          {variant.stock.onHand}
                        </span>
                        {variant.stock.onOrder > 0 ? (
                          <span className="order__onhand">+{variant.stock.onOrder} on order</span>
                        ) : null}
                        {variant.stock.reserved > 0 ? (
                          <span className="order__onhand">{variant.stock.reserved} held</span>
                        ) : null}
                        {boxFacts(variant, shown).map((fact) => (
                          <span key={fact.key} className="size-box__meta">
                            {fact.key === 'sku' ? <Mono>{fact.text}</Mono> : fact.text}
                          </span>
                        ))}
                      </>
                    );
                    return (
                      <td
                        key={variant.id}
                        className={scanned ? 'size-box size-box--scanned' : 'size-box'}
                        aria-current={scanned ? 'true' : undefined}
                      >
                        {onOpen === null ? (
                          <span className="size-box__inner">{inside}</span>
                        ) : (
                          <button
                            type="button"
                            className="size-box__inner size-box__button"
                            aria-label={`${where}: ${variant.stock.onHand} on hand, ${variant.sku} — edit`}
                            onClick={() => onOpen(variant.id)}
                          >
                            {inside}
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </>
  );
}

/** The lines under a box's count, in the list's order, each saying what it is. */
function boxFacts(
  variant: { sku: string; retailCents: number; costCents: number; msrpCents: number | null },
  shown: readonly GridField[],
): { key: GridField; text: string }[] {
  const margin = marginPercent(variant.costCents, variant.retailCents);
  const facts: { key: GridField; text: string }[] = [];
  if (shown.includes('sku')) facts.push({ key: 'sku', text: variant.sku });
  if (shown.includes('retail')) facts.push({ key: 'retail', text: money(variant.retailCents) });
  if (shown.includes('cost')) facts.push({ key: 'cost', text: `Cost ${money(variant.costCents)}` });
  if (shown.includes('margin'))
    facts.push({
      key: 'margin',
      text: margin === null ? 'Margin —' : `Margin ${margin.toFixed(0)}%`,
    });
  if (shown.includes('msrp'))
    facts.push({
      key: 'msrp',
      text: variant.msrpCents === null ? 'MSRP —' : `MSRP ${money(variant.msrpCents)}`,
    });
  return facts;
}
